import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { inject } from '@app/providers/QAU.ContextProvider';
import LocalizedText from '@LocalizedText';

import Button from '@components/buttons/Button';
import ZoomControls from './zoom-controls/ZoomControls';
import './ZoomTool.scss';
import { zoomToIndex, zoomToPercent } from '@app/utils/utils';
import Slider from '@components/slider/Slider';
import useFocusTrap from '@app/hooks/use-focus-trap';

const ZoomTool = ({ currentZoom, minZoom, maxZoom, setZoomLevel, scrollToCenter, autoZoom }) => {
  const [zoomToInputValue, setZoomToInputValue] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const zoomContainerRef = useRef();
  const zoomButtonRef = useRef();

  const toggleZoomPopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  const closePopup = () => {
    const zoomToInputValueIndex = zoomToInputValue && zoomToIndex(zoomToInputValue);
    const shouldUpdateZoom = zoomToInputValueIndex && currentZoom && currentZoom !== zoomToInputValueIndex;

    if (shouldUpdateZoom) {
      setZoomLevel(zoomToInputValueIndex);
      scrollToCenter?.();
    }

    setIsPopupOpen(false);
    zoomButtonRef.current?.focus();
  };

  const closeZoomPopup = ({ target }) => {
    const isZoomPopupBody = zoomContainerRef.current?.contains(target);
    const isZoomToggleBtn = target.classList?.contains('zoom-toggle-btn');

    if (isZoomToggleBtn || isZoomPopupBody || !isPopupOpen) return;

    closePopup();
  };

  useFocusTrap(zoomContainerRef, isPopupOpen, {
    triggerRef: zoomButtonRef,
    onEscape: closePopup,
    autoFocus: true,
    isTriggerFocusable: true,
  });

  useEffect(() => {
    document.body.addEventListener('click', closeZoomPopup);
    scrollToCenter && window.addEventListener('resize', scrollToCenter);
    return () => {
      document.body.removeEventListener('click', closeZoomPopup);
      scrollToCenter && window.removeEventListener('resize', scrollToCenter);
    };
  });

  const onZoomChanged = value => {
    setZoomLevel(value);
    scrollToCenter?.();
  };

  if (!currentZoom || !minZoom || !maxZoom) return null;

  return (
    <div id="zoom-tool">
      <Button ref={zoomButtonRef} className="zoom-toggle-btn" onClick={toggleZoomPopup}>
        <LocalizedText id="qau-tool-zoom" values={{ zoomValue: (currentZoom * 100).toFixed() }} />
      </Button>
      <div ref={zoomContainerRef} className={`zoom-container ${isPopupOpen ? 'active' : ''}`} role="dialog" aria-modal="true">
        <Slider
          minValue={minZoom}
          maxValue={maxZoom}
          step={0.05}
          defaultValue={currentZoom}
          filter={zoomToPercent}
          onChange={onZoomChanged}
          onChangeValueByStep={onZoomChanged}
          maxValueLocaleKey="qau-slider-bound-max"
          minValueLocaleKey="qau-slider-bound-min"
          ariaLabelLocaleKey="qau-zoom-slider-aria-label"
        />
        <ZoomControls
          currentZoom={currentZoom}
          minZoom={minZoom}
          maxZoom={maxZoom}
          setZoomLevel={setZoomLevel}
          scrollToCenter={scrollToCenter}
          autoZoom={autoZoom}
          zoomToInputValue={zoomToInputValue}
          setZoomToInputValue={setZoomToInputValue}
        />
      </div>
    </div>
  );
};

ZoomTool.propTypes = {
  currentZoom: PropTypes.number.isRequired,
  minZoom: PropTypes.number.isRequired,
  maxZoom: PropTypes.number.isRequired,
  scrollToCenter: PropTypes.func,
  setZoomLevel: PropTypes.func.isRequired,
  autoZoom: PropTypes.func.isRequired,
};

export default inject('flow')(ZoomTool);
